import _ from 'lodash';
import { PropsWithChildren } from 'react';
import { FormikProvider, useField, useFormik } from 'formik';
import { useTheme } from 'styled-components';
import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { rgba } from 'polished';

import { useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import { ActionArchiveListItem } from 'packages/kakapo-ui-kit/components/actions/ActionListItem';
import {
	PageTitle,
	SectionTitle,
	SubSectionTitle,
} from 'packages/kakapo-ui-kit/components/common/Headings';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import DetailsTableForm from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import Button from 'packages/kakapo-ui-kit/components/editors/Button';
import useSupportedLanguageIDs from 'packages/kakapo-ui-application/hooks/useSupportedLanguageIDs';
import Select from 'packages/kakapo-ui-kit/components/forms/controls/Select';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import useErrorBoundary from 'packages/kakapo-ui-application/hooks/useErrrorBoundary';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import useLocalizedTranslation, {
	withLocalizedTranslation,
} from 'packages/kakapo-ui-application/hooks/useLocalizedTranslation';
import Paragraph from 'packages/kakapo-ui-kit/components/common/Paragraph';

import TalentReviewGrid from './TalentReviewGrid';

function TalentReviewLanguageSelector() {
	const { t } = useTranslation();
	const [{ value: languageID }, , { setValue: setLanguageID }] = useField('languageID');
	const ids = useSupportedLanguageIDs();

	const options = _.sortBy(
		ids?.map((id) => ({
			label: t(`common.languages.${id}`),
			value: id,
		})) ?? [],
		({ label }) => label,
	);

	return (
		<Box width={200}>
			<Select
				options={options}
				value={options.find(({ value }) => value === languageID)}
				onChange={(option) => setLanguageID(option!.value)}
				small
				unspaced
			/>
		</Box>
	);
}

const Warning = styled(Box)`
	background: ${(props) => rgba(props.theme.colors.yellow, 0.25)};
	border-radius: 4px;
`;

const OptionTitle = styled(Paragraph)`
	font-weight: bold;
	color: black;
`;

function VisibilityOption(
	props: PropsWithChildren<{ title: string; value: graphql.TalentReviewVisibility }>,
) {
	const [{ value }, , { setValue }] = useField<graphql.TalentReviewVisibility>('visibility');

	return (
		<Flex
			alignItems="center"
			width={600}
			py={2}
			onClick={() => setValue(props.value)}
			style={{ cursor: 'pointer' }}
		>
			<Box mr={3} flexShrink={0}>
				<input
					type="radio"
					style={{ display: 'block' }}
					checked={value === props.value}
					onChange={() => setValue(props.value)}
				/>
			</Box>
			<Box>
				<OptionTitle>{props.title}</OptionTitle>
				<Paragraph color="slateBlue">{props.children}</Paragraph>
			</Box>
		</Flex>
	);
}

const TalentReviewEditor = withLocalizedTranslation(function TalentReviewEditor(props: {
	template?: graphql.TalentReviewTemplate;
	localizedT?: ReturnType<typeof useLocalizedTranslation>['localizedT'];
}) {
	const { t } = useTranslation();
	const { language } = useLocalize();
	const theme = useTheme() as any;

	const red = parseInt(theme.colors.red.slice(1), 16);
	const yellow = parseInt(theme.colors.yellow.slice(1), 16);
	const green = parseInt(theme.colors.green.slice(1), 16);

	const formik = useFormik({
		initialValues: {
			languageID: language.id,
			title: props.template?.title ?? [],
			description: props.template?.description ?? [],
			visibility: props.template?.visibility ?? graphql.TalentReviewVisibility.Self,
			xAxis: {
				label:
					props.template?.xAxis.label ??
					props.localizedT!('settings.talentReviews.default.xAxis.label'),
				ticks: props.template?.xAxis.ticks ?? [
					props.localizedT!('settings.talentReviews.default.xAxis.ticks.0'),
					props.localizedT!('settings.talentReviews.default.xAxis.ticks.1'),
					props.localizedT!('settings.talentReviews.default.xAxis.ticks.2'),
				],
			},
			yAxis: {
				label:
					props.template?.yAxis.label ??
					props.localizedT!('settings.talentReviews.default.yAxis.label'),
				ticks: props.template?.yAxis.ticks ?? [
					props.localizedT!('settings.talentReviews.default.yAxis.ticks.0'),
					props.localizedT!('settings.talentReviews.default.yAxis.ticks.1'),
					props.localizedT!('settings.talentReviews.default.yAxis.ticks.2'),
				],
			},
			cells: props.template?.cells.map((row) => row.map((cell) => _.omit(cell, '__typename'))) ?? [
				[
					{ color: red, label: [], instruction: [] },
					{ color: red, label: [], instruction: [] },
					{ color: red, label: [], instruction: [] },
				],
				[
					{ color: red, label: [], instruction: [] },
					{ color: yellow, label: [], instruction: [] },
					{ color: yellow, label: [], instruction: [] },
				],
				[
					{ color: red, label: [], instruction: [] },
					{ color: yellow, label: [], instruction: [] },
					{ color: green, label: [], instruction: [] },
				],
			],
		},
		onSubmit: () => {},
	});

	const [create, { loading: createLoading }] = graphql.useCreateTalentReviewTemplateMutation();
	const [update, { loading: updateLoading }] = graphql.useUpdateTalentReviewTemplateMutation();

	const loading = createLoading || updateLoading;

	const history = useHistory();

	const [error, attempt] = useErrorBoundary();

	const onSave = attempt(async () => {
		const input: graphql.CreateTalentReviewTemplateInput = {
			title: formik.values.title,
			description: formik.values.description,
			visibility: formik.values.visibility,
			xAxis: formik.values.xAxis,
			yAxis: formik.values.yAxis,
			cells: formik.values.cells,
		};

		if (props.template) {
			await update({ variables: { input: { ...input, templateID: props.template.id } } });
		} else {
			const response = await create({ variables: { input } });

			history.push(
				`/settings/talent-reviews/${response.data!.createTalentReviewTemplate.template.id}`,
			);
		}
	});

	return (
		<FormikProvider value={formik}>
			<Flex flexDirection="row" alignItems="center">
				<PageTitle>
					{t(`settings.talentReviews.${props.template ? 'edit' : 'create'}.title`)}
				</PageTitle>
			</Flex>
			<Box mt={4} mb={2}>
				<Box mb={3}>
					<SubSectionTitle>{t('settings.talentReviews.edit.details')}</SubSectionTitle>
				</Box>
				<HorizontalRule />
			</Box>
			<GraphQLErrorMessage error={error} />
			<DetailsTableForm
				formValues={[
					{
						label: t('settings.talentReviews.edit.name'),
						name: 'title',
						type: DetailsTableFormValueTypes.Multilingual,
					},
					{
						label: t('settings.talentReviews.edit.description'),
						name: 'description',
						type: DetailsTableFormValueTypes.MultilingualQuill,
					},
				]}
			/>

			<Box mt={4} mb={2}>
				<Box mb={3}>
					<SubSectionTitle>{t('settings.talentReviews.edit.visibility.label')}</SubSectionTitle>
				</Box>
				<HorizontalRule />
			</Box>

			<Warning p={3} mb={2}>
				<Paragraph color="black">{t('settings.talentReviews.edit.visibility.warning')}</Paragraph>
			</Warning>

			<VisibilityOption
				value={graphql.TalentReviewVisibility.Members}
				title={t('settings.talentReviews.edit.visibility.everyone.title')}
			>
				{t('settings.talentReviews.edit.visibility.everyone.description')}
			</VisibilityOption>

			<VisibilityOption
				value={graphql.TalentReviewVisibility.Self}
				title={t('settings.talentReviews.edit.visibility.self.title')}
			>
				{t('settings.talentReviews.edit.visibility.self.description')}
			</VisibilityOption>

			<VisibilityOption
				value={graphql.TalentReviewVisibility.Manager}
				title={t('settings.talentReviews.edit.visibility.manager.title')}
			>
				{t('settings.talentReviews.edit.visibility.manager.description')}
			</VisibilityOption>

			<Box width="fit-content" pr={3}>
				<Box mt={4} mb={2}>
					<Flex mb={3} alignItems="center">
						<Box flexGrow={1} flexShrink={1}>
							<SubSectionTitle>
								{formik.values.xAxis.ticks.length * formik.values.yAxis.ticks.length}{' '}
								{t('settings.talentReviews.edit.grid')}
							</SubSectionTitle>
						</Box>
						<Box flexGrow={0} flexShrink={0}>
							<TalentReviewLanguageSelector />
						</Box>
					</Flex>
					<HorizontalRule />
				</Box>

				<TalentReviewGrid existing={!!props.template} />
			</Box>

			<Flex mt={4}>
				<Button size={32} onClick={() => onSave()} isLoading={loading}>
					{t('common.save')}
				</Button>
			</Flex>

			{props.template && (
				<Box mt={4}>
					<Box my={2}>
						<SectionTitle>{t('settings.talentReviews.actions.title')}</SectionTitle>
					</Box>

					<Box mb={3}>
						<HorizontalRule />
					</Box>

					<ActionArchiveListItem
						archivableItem={props.template}
						archivableItemName={t('settings.talentReviews.archivableItemName')}
						archivableItemKey="jobTitle"
						useArchive={graphql.useArchiveTalentReviewTemplateMutation as any}
						useRestore={graphql.useRestoreTalentReviewTemplateMutation as any}
					/>
				</Box>
			)}
		</FormikProvider>
	);
});

export default function TalentReviewEditorWithID(props: { id?: string }) {
	const query = graphql.useTalentReviewTemplateQuery({
		variables: { id: props.id! },
		skip: !props.id,
	});

	if (!props.id) {
		return <TalentReviewEditor />;
	} else if (query.data) {
		return (
			<TalentReviewEditor
				template={query.data.talentReviewTemplate as graphql.TalentReviewTemplate}
			/>
		);
	} else {
		return <Spinner isLoading size="18px" />;
	}
}
